/**
 * SamplingDetailsDialog — "Sample Details" editable form, converted 1:1 from
 * Access `frmQualityQrySamplingAddInfo` ("Sample - Info"). Opened on the selected
 * row only (Edit-only) → writes the Info subset of tlc_factory.sampling via the
 * shared PUT /quality-samplings/{id}. 2-col grid (Access Left 3312 → col 1,
 * 9792 → col 2), row order by Top, two section bands ("Lot's Information" /
 * "Sample Sending Information").
 *
 * Required (strCheck): sample_no (read-only, auto), tlc_contract_ref, ps_date,
 * ps_sent_date, warehouse_code, client_lot_id, lot_size_mt.
 * Cascades:
 *   tlc_contract_ref → snapshot ~13 fields from latest prior row of same contract
 *   license_cert_no  → cert_unit (cp_vn.short_name)
 *   warehouse_code   → lot_id options (TLBL/TLNC/TLNT lot_mgt; else free text)
 *
 * Field order (Access Top):
 *   sample_no | ps_date
 *   tlc_contract_ref | do_no
 *   — Lot's Information —
 *   warehouse_code | client_lot_id
 *   lot_id | weight_per_cont_mt
 *   lot_size_mt | cert_unit
 *   license_cert_no | grade
 *   origin |
 *   — Sample Sending Information —
 *   qty_preshipment_client | ps_sent_date
 *   cost | qty_preshipment_supervisor
 *   qty_aftershipment_client | qty_aftershipment_supervisor
 *   receiver | seal_no
 *   inspection_by | receiving_authority
 *   note (span 2)
 */
import { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type SamplingLookupItem,
  type SamplingWrite,
  useLazyGetSamplingFormQuery,
  useLazyLkSamplingCertUnitQuery,
  useLazyLkSamplingContractSnapshotQuery,
  useUpdateSamplingMutation,
  useLkSamplingTlcContractsQuery,
  useLkSamplingDoNosQuery,
  useLkSamplingWarehousesQuery,
  useLkSamplingLotsQuery,
  useLkSamplingLicensesQuery,
  useLkSamplingOriginsQuery,
} from '../../store/api/samplingFormApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface Props {
  open: boolean
  samplingId: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  sampleNo: string
  psDate: string
  tlcContractRef: string
  doNo: string
  warehouseCode: string
  clientLotId: string
  lotId: string
  weightPerContMt: string
  lotSizeMt: string
  certUnit: string
  licenseCertNo: string
  grade: string
  origin: string
  qtyPreshipmentClient: string
  psSentDate: string
  cost: string
  qtyPreshipmentSupervisor: string
  qtyAftershipmentClient: string
  qtyAftershipmentSupervisor: string
  receiver: string
  sealNo: string
  inspectionBy: string
  receivingAuthority: string
  note: string
}

const EMPTY: Draft = {
  sampleNo: '', psDate: '', tlcContractRef: '', doNo: '', warehouseCode: '', clientLotId: '',
  lotId: '', weightPerContMt: '', lotSizeMt: '', certUnit: '', licenseCertNo: '', grade: '',
  origin: '', qtyPreshipmentClient: '', psSentDate: '', cost: '', qtyPreshipmentSupervisor: '',
  qtyAftershipmentClient: '', qtyAftershipmentSupervisor: '', receiver: '', sealNo: '',
  inspectionBy: '', receivingAuthority: '', note: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
}
const grid2Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const t = (v ?? '').toString().replace(/,/g, '').trim()
  if (!t) return null
  const n = parseFloat(t)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))

/* ── Reusable combo (Autocomplete flat "<CODE> — <NAME>", injects current value) ── */
function Combo(props: {
  label: string
  value: string
  valueLabel?: string
  options: SamplingLookupItem[]
  loading?: boolean
  required?: boolean
  freeSolo?: boolean
  labelOnly?: boolean
  error?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string, item: SamplingLookupItem | null) => void
}) {
  const { label, value, valueLabel, options, loading, required, freeSolo, labelOnly, error, onInputChange, onPick } = props
  const sel = value
    ? options.find((o) => o.value === value) ?? { value, label: valueLabel || value }
    : null
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  const getLabel = (o: SamplingLookupItem | string) => {
    if (typeof o === 'string') return o
    if (!o) return ''
    return labelOnly || o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`
  }
  return (
    <Autocomplete
      size={SM}
      freeSolo={freeSolo}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => {
        if (typeof v === 'string') onPick(v, null)
        else onPick(v?.value ?? '', v)
      }}
      onInputChange={(_e, v, reason) => {
        if (freeSolo && reason === 'input') onPick(v, null)
        else if (onInputChange && reason === 'input') onInputChange(v)
      }}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(a, b) =>
        (typeof a === 'string' ? a : a.value) === (typeof b === 'string' ? b : b.value)}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => (
        <TextField {...params} label={required ? `${label} *` : label} error={error} />
      )}
    />
  )
}

export default function SamplingDetailsDialog({ open, samplingId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [labels, setLabels] = useState<Record<string, string>>({})
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [loadForm, { isFetching: hydrating }] = useLazyGetSamplingFormQuery()
  const [fetchCertUnit] = useLazyLkSamplingCertUnitQuery()
  const [fetchSnapshot] = useLazyLkSamplingContractSnapshotQuery()
  const [updateSampling, { isLoading: saving }] = useUpdateSamplingMutation()

  const skip = { skip: !open }
  const [scQ, setScQ] = useState('')
  const [doQ, setDoQ] = useState('')
  const tlcContracts = useLkSamplingTlcContractsQuery(scQ || undefined, skip)
  const doNos = useLkSamplingDoNosQuery(doQ || undefined, skip)
  const warehouses = useLkSamplingWarehousesQuery(undefined, skip)
  const licenses = useLkSamplingLicensesQuery(undefined, skip)
  const origins = useLkSamplingOriginsQuery(undefined, skip)
  // lot_id options cascade off warehouse_code (TLBL/TLNC/TLNT → list; else free text)
  const lotWh = draft.warehouseCode
  const isLotList = ['TLBL', 'TLNC', 'TLNT'].includes(lotWh.toUpperCase())
  const lots = useLkSamplingLotsQuery({ warehouseCode: lotWh || undefined }, { skip: !open || !isLotList })

  // Hydrate on open (Edit-only)
  useEffect(() => {
    if (!open || !samplingId) return
    let cancelled = false
    void (async () => {
      const f = await loadForm(samplingId).unwrap().catch(() => null)
      if (cancelled || !f) return
      const r = f.record as unknown as Record<string, unknown>
      const g = (k: string) => s(r[k])
      setLabels({ tlcContractRef: g('buyersRef'), warehouseCode: g('whSamplingName') })
      setDraft({
        sampleNo: g('sampleNo'),
        psDate: g('psDate').slice(0, 10),
        tlcContractRef: g('tlcContractRef'), doNo: g('doNo'),
        warehouseCode: g('warehouseCode'), clientLotId: g('clientLotId'), lotId: g('lotId'),
        weightPerContMt: fmtStd(g('weightPerContMt')), lotSizeMt: fmtStd(g('lotSizeMt')),
        certUnit: g('certUnit'), licenseCertNo: g('licenseCertNo'), grade: g('grade'),
        origin: g('origin'),
        qtyPreshipmentClient: fmtStd(g('qtyPreshipmentClient')),
        psSentDate: g('psSentDate').slice(0, 10),
        cost: fmtStd(g('cost')),
        qtyPreshipmentSupervisor: fmtStd(g('qtyPreshipmentSupervisor')),
        qtyAftershipmentClient: fmtStd(g('qtyAftershipmentClient')),
        qtyAftershipmentSupervisor: fmtStd(g('qtyAftershipmentSupervisor')),
        receiver: g('receiver'), sealNo: g('sealNo'), inspectionBy: g('inspectionBy'),
        receivingAuthority: g('receivingAuthority'), note: g('note'),
      })
    })()
    return () => { cancelled = true }
  }, [open, samplingId, loadForm])

  // tlc_contract_ref change → snapshot ~13 fields from latest prior row of same contract
  const handleContractChange = (value: string, item: SamplingLookupItem | null) => {
    setDraft((p) => ({ ...p, tlcContractRef: value }))
    setLabels((p) => ({ ...p, tlcContractRef: item?.label ?? '' }))
    if (!value) return
    void fetchSnapshot(value).unwrap()
      .then((snap) => {
        if (!snap || Object.keys(snap).length === 0) return
        setDraft((p) => ({
          ...p,
          psDate: snap.psDate ? snap.psDate.slice(0, 10) : p.psDate,
          warehouseCode: snap.warehouseCode ?? p.warehouseCode,
          lotSizeMt: snap.lotSizeMt != null ? fmtStd(snap.lotSizeMt) : p.lotSizeMt,
          weightPerContMt: snap.weightPerContMt != null ? fmtStd(snap.weightPerContMt) : p.weightPerContMt,
          licenseCertNo: snap.licenseCertNo ?? p.licenseCertNo,
          origin: snap.origin ?? p.origin,
          psSentDate: snap.psSentDate ? snap.psSentDate.slice(0, 10) : p.psSentDate,
          receiver: snap.receiver ?? p.receiver,
          receivingAuthority: snap.receivingAuthority ?? p.receivingAuthority,
          inspectionBy: snap.inspectionBy ?? p.inspectionBy,
          sealNo: snap.sealNo ?? p.sealNo,
          certUnit: snap.certUnit ?? p.certUnit,
          grade: snap.grade ?? p.grade,
        }))
        if (snap.warehouseCode) setLabels((p) => ({ ...p, warehouseCode: '' }))
      })
      .catch(() => undefined)
  }

  // license_cert_no change → cert_unit snapshot
  const handleLicenseChange = (value: string) => {
    setDraft((p) => ({ ...p, licenseCertNo: value }))
    if (!value) return
    void fetchCertUnit(value).unwrap()
      .then((res) => set('certUnit', res.certUnit ?? ''))
      .catch(() => undefined)
  }

  // warehouse_code change → reset lot_id when leaving list source / changing wh
  const handleWarehouseChange = (value: string, item: SamplingLookupItem | null) => {
    setDraft((p) => ({ ...p, warehouseCode: value, lotId: '' }))
    setLabels((p) => ({ ...p, warehouseCode: item?.label ?? '' }))
  }

  const req = (v: string) => !v.trim()
  const canSave =
    canEdit && !saving && !hydrating &&
    !req(draft.tlcContractRef) && !req(draft.psDate) && !req(draft.psSentDate) &&
    !req(draft.warehouseCode) && !req(draft.clientLotId) && !req(draft.lotSizeMt)

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canSave || !samplingId) return
    const payload: SamplingWrite = {
      tlcContractRef: draft.tlcContractRef || null,
      psDate: draft.psDate || null,
      doNo: draft.doNo || null,
      warehouseCode: draft.warehouseCode || null,
      lotId: draft.lotId || null,
      lotSizeMt: parseNum(draft.lotSizeMt),
      licenseCertNo: draft.licenseCertNo || null,
      origin: draft.origin || null,
      clientLotId: draft.clientLotId || null,
      weightPerContMt: parseNum(draft.weightPerContMt),
      certUnit: draft.certUnit || null,
      grade: draft.grade || null,
      qtyPreshipmentClient: parseNum(draft.qtyPreshipmentClient),
      qtyPreshipmentSupervisor: parseNum(draft.qtyPreshipmentSupervisor),
      cost: parseNum(draft.cost),
      qtyAftershipmentClient: parseNum(draft.qtyAftershipmentClient),
      qtyAftershipmentSupervisor: parseNum(draft.qtyAftershipmentSupervisor),
      receiver: draft.receiver || null,
      inspectionBy: draft.inspectionBy || null,
      note: draft.note || null,
      psSentDate: draft.psSentDate || null,
      receivingAuthority: draft.receivingAuthority || null,
      sealNo: draft.sealNo || null,
    }
    try {
      await updateSampling({ id: samplingId, body: payload }).unwrap()
      snack.success(`Sample ${draft.sampleNo} updated`)
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Save sample details failed'))
    }
  }

  const dateField = (key: keyof Draft, label: string, required?: boolean) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, required, InputLabelProps: { shrink: true }, error: required ? req(draft[key] as string) : undefined } }}
    />
  )
  const numField = (key: keyof Draft, label: string, required?: boolean) => (
    <TextField
      label={required ? `${label} *` : label} size={SM} fullWidth type="text" value={draft[key] as string}
      required={required} error={required ? req(draft[key] as string) : undefined}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      onBlur={() => set(key, fmtStd(draft[key] as string) as Draft[typeof key])}
      inputProps={numProps}
    />
  )
  const textField = (key: keyof Draft, label: string, required?: boolean, extra?: object) => (
    <TextField label={required ? `${label} *` : label} size={SM} fullWidth value={draft[key] as string}
      required={required} error={required ? req(draft[key] as string) : undefined}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])} {...extra} />
  )

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Sample - Info{draft.sampleNo ? ` — ${draft.sampleNo}` : ''}
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit sampling data.</Alert>}
        {hydrating ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {/* ── Sample - Info header band ── */}
            <Box sx={grid2Sx}>
              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
                <TextField label="Sample No" size={SM} fullWidth value={draft.sampleNo}
                  InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx} />
              </Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>{dateField('psDate', 'PS Date', true)}</Box>

              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>
                <Combo label="TLC Contract Ref" value={draft.tlcContractRef} valueLabel={labels.tlcContractRef}
                  required error={req(draft.tlcContractRef)} options={tlcContracts.data?.items ?? []}
                  loading={tlcContracts.isFetching} onInputChange={setScQ} onPick={handleContractChange} />
              </Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 2 } }}>
                <Combo label="DO No" value={draft.doNo} labelOnly options={doNos.data?.items ?? []}
                  loading={doNos.isFetching} onInputChange={setDoQ} onPick={(v) => set('doNo', v)} />
              </Box>
            </Box>

            {/* ── Lot's Information ── */}
            <Typography sx={sectionTitleSx}>Lot&apos;s Information</Typography>
            <Box sx={grid2Sx}>
              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
                <Combo label="Warehouse Code" value={draft.warehouseCode} valueLabel={labels.warehouseCode}
                  required error={req(draft.warehouseCode)} options={warehouses.data?.items ?? []}
                  loading={warehouses.isFetching} onPick={handleWarehouseChange} />
              </Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>{textField('clientLotId', 'Client Lot ID', true)}</Box>

              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>
                {/* Access warehouse_code_AfterUpdate: TLBL/TLNC/TLNT → combo; else free text. */}
                <Combo label="Lot ID" value={draft.lotId} freeSolo={!isLotList} labelOnly
                  options={isLotList ? lots.data?.items ?? [] : []} loading={lots.isFetching}
                  onPick={(v) => set('lotId', v)} />
              </Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 2 } }}>{numField('weightPerContMt', 'Weight Per Cont Mt')}</Box>

              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 3 } }}>{numField('lotSizeMt', 'Lot Size Mt', true)}</Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 3 } }}>{textField('certUnit', 'Cert Unit')}</Box>

              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 4 } }}>
                <Combo label="License Cert No" value={draft.licenseCertNo} labelOnly
                  options={licenses.data?.items ?? []} loading={licenses.isFetching} onPick={handleLicenseChange} />
              </Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 4 } }}>{textField('grade', 'Grade')}</Box>

              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 5 } }}>
                <Combo label="Origin" value={draft.origin} labelOnly options={origins.data?.items ?? []}
                  loading={origins.isFetching} onPick={(v) => set('origin', v)} />
              </Box>
            </Box>

            {/* ── Sample Sending Information ── */}
            <Typography sx={sectionTitleSx}>Sample Sending Information</Typography>
            <Box sx={grid2Sx}>
              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>{numField('qtyPreshipmentClient', 'Qty Preshipment Client')}</Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 1 } }}>{dateField('psSentDate', 'PS Sent Date', true)}</Box>

              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>{numField('cost', 'Cost')}</Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 2 } }}>{numField('qtyPreshipmentSupervisor', 'Qty Preshipment Supervisor')}</Box>

              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 3 } }}>{numField('qtyAftershipmentClient', 'Qty Aftershipment Client')}</Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 3 } }}>{numField('qtyAftershipmentSupervisor', 'Qty Aftershipment Supervisor')}</Box>

              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 4 } }}>{textField('receiver', 'Receiver')}</Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 4 } }}>{textField('sealNo', 'Seal No')}</Box>

              <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 5 } }}>{textField('inspectionBy', 'Inspection By')}</Box>
              <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: 5 } }}>{textField('receivingAuthority', 'Receiving Authority')}</Box>

              <Box sx={{ gridColumn: { md: '1 / -1' }, gridRow: { md: 6 } }}>
                {textField('note', 'Note', false, { multiline: true, minRows: 3 })}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
